import { useRef, useState } from 'react'

import { useResizeObserver } from '../../../../hooks'
import { PdfViewerFitMode } from '../../PdfViewer.constants'

import {
  PDF_VIEWER_MAX_SCALE,
  PDF_VIEWER_MIN_SCALE,
} from './usePdfViewerScale.constants'
import {
  checkHasElementSize,
  checkIsContainerSizeChanged,
  checkIsPageFitMode,
  checkIsScaleFitMode,
  setPdfViewerPageWidthVariable,
} from './usePdfViewerScale.utils'

export function usePdfViewerScale({
  containerRef,
  fitMode = PdfViewerFitMode.PageWidth,
  pdfViewerRef,
  viewerRootRef,
}) {
  const [scale, setScale] = useState(null)
  const [scaleValue, setScaleValue] = useState(fitMode)

  const lastContainerSizeRef = useRef(null)
  const fitModeValueRef = useRef(fitMode)

  function fitPagesToContainer() {
    const pdfViewer = pdfViewerRef.current

    if (
      pdfViewer?.pdfDocument &&
      fitModeValueRef.current &&
      checkHasElementSize(containerRef.current)
    ) {
      pdfViewer.currentScaleValue = fitModeValueRef.current
    }
  }

  function publishPageWidth() {
    const pdfViewer = pdfViewerRef.current

    if (pdfViewer) {
      const pageView = pdfViewer.getPageView(pdfViewer.currentPageNumber - 1)

      setPdfViewerPageWidthVariable({
        hostElement: viewerRootRef.current,
        pageWidth: pageView?.width,
      })
    }
  }

  function handleContainerResize(element, entry) {
    const [{ blockSize, inlineSize }] = entry.borderBoxSize
    const nextSize = { blockSize, inlineSize }

    if (
      checkIsContainerSizeChanged({
        lastSize: lastContainerSizeRef.current,
        nextSize,
      })
    ) {
      lastContainerSizeRef.current = nextSize

      // a refit inside the observer tick trips the "ResizeObserver loop" error
      requestAnimationFrame(fitPagesToContainer)
    }
  }

  useResizeObserver({
    callback: handleContainerResize,
    ref: containerRef,
  })

  function handleScaleChanging(event) {
    setScale(event.scale)
    publishPageWidth()
  }

  function handleChangeScaleValue(nextScaleValue) {
    fitModeValueRef.current = checkIsScaleFitMode(nextScaleValue)
      ? nextScaleValue
      : null

    setScaleValue(nextScaleValue)

    pdfViewerRef.current.currentScaleValue = String(nextScaleValue)
  }

  function stepScale(step) {
    fitModeValueRef.current = null

    setScaleValue(null)
    step()
  }

  function handleZoomIn() {
    stepScale(() => pdfViewerRef.current.increaseScale())
  }

  function handleZoomOut() {
    stepScale(() => pdfViewerRef.current.decreaseScale())
  }

  function resetScale() {
    fitModeValueRef.current = fitMode

    setScaleValue(fitMode)
  }

  return {
    fitPagesToContainer,
    isPageFitMode: checkIsPageFitMode(scaleValue),
    publishPageWidth,
    resetScale,
    scaleProps: {
      isZoomInDisabled: scale >= PDF_VIEWER_MAX_SCALE,
      isZoomOutDisabled: scale <= PDF_VIEWER_MIN_SCALE,
      scale,
      scaleValue,
      onChangeScaleValue: handleChangeScaleValue,
      onZoomIn: handleZoomIn,
      onZoomOut: handleZoomOut,
    },
    onScaleChanging: handleScaleChanging,
  }
}
